// Auth client for the public site. Talks to the gazar backend's /api/auth + /api/me.
// The JWT is the session, held in an httpOnly `token` cookie set by the API at
// login (page JS can't read it, so an XSS payload can't steal it). Browser calls
// send it automatically via `credentials: "include"`; SSR loaders read it off the
// incoming request's Cookie header (see tokenFromRequest) and forward it.
import { apiBase } from "./content";

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
}

// Read the session token out of a loader's Request during SSR: the httpOnly
// cookie is on the incoming request's Cookie header (readable server-side) even
// though page JS can't see it. On the client the cookie is sent automatically
// (credentials: "include"), so no token needs to be extracted there.
export function tokenFromRequest(request: Request): string | undefined {
  const cookie = request.headers.get("cookie");
  if (cookie) {
    const m = cookie.match(/(?:^|;\s*)token=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
  }
  return undefined;
}

async function authFetch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${apiBase()}/api/auth${path}`, {
    method: "POST",
    // Only declare a JSON body when there actually is one. A bodyless POST
    // (logout) sent with Content-Type: application/json makes Fastify 400 on the
    // empty body, which returns before the route clears the session cookie — so
    // "sign out" would look like it worked but leave you logged in on reload.
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    credentials: "include", // send/receive the httpOnly session cookie
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message || "Request failed");
  return data as T;
}

export interface SignupInput {
  email: string;
  password: string;
  name?: string;
  acceptedTerms: true;
  marketingConsent?: boolean;
}

export const authApi = {
  login: (email: string, password: string) =>
    authFetch<{ token: string; user: AuthUser }>("/login", { email, password }),
  signup: (input: SignupInput) =>
    authFetch<{ token: string; user: AuthUser }>("/signup", input),
  logout: () => authFetch<{ ok: boolean }>("/logout").catch(() => ({ ok: true })),
  me: async (): Promise<{ user: AuthUser; entitlements: string[]; canDraw: boolean } | null> => {
    // A failed auth probe just means "not signed in" — never a page crash. Catch
    // network/CORS failures (fetch rejects with a TypeError) as well as non-2xx
    // so this can't surface as an unhandled rejection (was flooding GlitchTip
    // from first-party subdomains before their origins were CORS-allowlisted).
    try {
      const res = await fetch(`${apiBase()}/api/me`, { credentials: "include" });
      if (!res.ok) return null;
      const data = (await res.json()) as { user: AuthUser; entitlements: string[]; canDraw?: boolean };
      return { ...data, canDraw: data.canDraw ?? false };
    } catch {
      return null;
    }
  },
  /**
   * Where the reader has got to in a course.
   *
   * Returns null rather than throwing when it cannot be read. Progress is a
   * convenience on top of a course page that has to render either way: a signed
   * out reader, an expired session or a blip must show the curriculum, not an
   * error page.
   */
  courseProgress: async (
    slug: string,
  ): Promise<{ completed: string[]; total: number; resumeSlug: string | null } | null> => {
    try {
      const res = await fetch(`${apiBase()}/api/me/courses/${slug}/progress`, {
        credentials: "include",
      });
      if (!res.ok) return null;
      return (await res.json()) as { completed: string[]; total: number; resumeSlug: string | null };
    } catch {
      return null;
    }
  },

  /** Mark one lesson done or not done. Throws so the UI can put the tick back. */
  setLessonProgress: async (
    slug: string,
    lessonSlug: string,
    completed: boolean,
  ): Promise<void> => {
    const res = await fetch(`${apiBase()}/api/me/courses/${slug}/lessons/${lessonSlug}/progress`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ completed }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(data.message || "Could not save your progress.");
    }
  },

  checkout: async (slug: string): Promise<{ url: string }> => {
    const res = await fetch(`${apiBase()}/api/billing/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ slug }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { error?: string }).error || "Checkout failed");
    return data as { url: string };
  },
  // Mint a short-lived HLS playback token for a course. The response also sets the
  // `.gazar.dev` `pb` cookie the video Worker reads, so the media requests
  // authenticate automatically. Throws on 401/403 (not signed in / not entitled).
  playbackToken: async (courseSlug: string): Promise<{ token: string; exp: number; videoBase: string }> => {
    const res = await fetch(`${apiBase()}/api/video/courses/${courseSlug}/playback-token`, {
      method: "POST",
      credentials: "include",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as { message?: string }).message || "Playback unavailable");
    return data as { token: string; exp: number; videoBase: string };
  },
};
